import axios, { CancelToken } from "axios";
import { RuntimeRepository } from "core/repository";
import { getBaseAPI } from "../../util/get-base-api";

class BaseService {
  static _config = {
    baseUrl: ""
  };

  static sExpiredTokenCode = 2004;
  static sCancelToken = null;

  static config(config) {
    ["baseUrl", "authorizationToken"].forEach(key => {
      if (key in config) {
        BaseService._config[key] = config[key];
      }
    });
  }

  static getNewCancelToken() {
    const source = CancelToken.source();
    BaseService.sCancelToken = source;

    return source;
  }

  async get(url, config = {}) {
    let response, success;
    const wrappedConfig = this.wrapConfig(config);
    const cancelToken =
      BaseService.sCancelToken && BaseService.sCancelToken.token
        ? { cancelToken: BaseService.sCancelToken.token }
        : {};

    try {
      const baseURL = getBaseAPI()
      response = await axios.get(baseURL + url, {
        ...wrappedConfig,
        ...cancelToken
      });
      success = true;
    } catch (e) {
      if (e instanceof axios.Cancel) {
        e.response = {
          ...(e.response || {}),
          data: { error: { userCancelled: true } }
        };
      }
      response = e.response;
      success = false;
    }

    const fatal = !response || response.status >= 500;
    const data = fatal ? null : response.data;
    const headers = fatal ? null : response.headers;
    const errors =
      !!response && !!response.data.errors ? response.data.errors : [];

    return this.serviceResponse({ fatal, data, headers, success, errors });
  }

  async post(url, body = {}, config = {}) {
    let response, success;

    try {
      const baseURL = getBaseAPI()

      response = await axios.post(
        baseURL + url,
        body,
        this.wrapConfig(config)
      );
      success = true;
    } catch (e) {
      response = e.response;
      success = false;
    }

    const fatal = !response || response.status >= 500;
    const data = fatal ? null : response.data;
    const headers = fatal ? null : response.headers;
    const errors =
      !!response && !!response.data && !!response.data.errors
        ? response.data.errors
        : [];

    return this.serviceResponse({ fatal, data, headers, success, errors });
  }

  async update(url, body = {}, config = {}) {
    let response, success;

    try {
      const baseURL = getBaseAPI()
      response = await axios.patch(
        baseURL + url,
        body,
        this.wrapConfig(config)
      );
      success = true;
    } catch (e) {
      response = e.response;
      success = false;
    }

    const fatal = !response || response.status >= 500;
    const data = fatal ? null : response.data;
    const headers = fatal ? null : response.headers;
    const errors =
      !!response && !!response.data && !!response.data.errors
        ? response.data.errors
        : [];

    return this.serviceResponse({ fatal, data, headers, success, errors });
  }

  async destroy(url, config = {}) {
    let response, success;
    try {
      const baseURL = getBaseAPI()
      response = await axios.delete(
        baseURL + url,
        this.wrapConfig(config)
      );
      success = true;
    } catch (e) {
      response = e.response;
      success = false;
    }
    const fatal = !response || response.status >= 500;
    const data = fatal ? null : response.data;
    const headers = fatal ? null : response.headers;
    const errors =
      !!response && !!response.data && !!response.data.errors
        ? response.data.errors
        : [];

    return this.serviceResponse({ fatal, data, headers, success, errors });
  }

  wrapConfig(config) {
    const headers = {};

    if (!!BaseService._config.authorizationToken) {
      headers[
        "Authorization"
      ] = `Bearer ${BaseService._config.authorizationToken}`;
    }

    return {
      ...config,
      headers
    };
  }

  serviceResponse(response) {
    if (response.success) {
      return this.success({ data: response.data, headers: response.headers });
    } else if (response.fatal) {
      return this.fatal();
    } else {
      const data = response.data;

      if (data && data.code === BaseService.sExpiredTokenCode) {
        RuntimeRepository.getInstance().then(repository => {
          repository.unset("token");
          repository.fire(RuntimeRepository.props.logoutByInactivity);
        });

        delete BaseService._config.authorizationToken;
      }

      return this.error({
        data: data,
        content: data.error || data.message || data.errors,
        errors: data.errors
      });
    }
  }

  success({ data, headers }) {
    return {
      data,
      headers,
      success: true,
      fatal: false,
      userCancelled: false
    };
  }

  error({ data, content, errors }) {
    return {
      errors,
      error: content,
      data: data,
      success: false,
      fatal: false,
      userCancelled: ((data || {}).error || {}).userCancelled
    };
  }

  fatal() {
    return {
      success: false,
      fatal: true,
      data: {},
      userCancelled: false
    };
  }

  //============================================================================
  // ONLINE STREAM STUFF
  //============================================================================

  async stream(
    companyUuid,
    { order, filters, group, checkUserCancellation = false },
    callback
  ) {
    if (!callback) {
      if (checkUserCancellation) {
        return { success: true };
      } else {
        return true;
      }
    }

    let totalPages = 1;

    for (let page = 1; page <= totalPages; page++) {
      const result = await this.fetch(companyUuid, {
        filters,
        order,
        group,
        pagination: { page }
      });

      if (!result.success) {
        if (checkUserCancellation) {
          return { success: false, userCancelled: result.userCancelled };
        } else {
          return false;
        }
      }

      for (const registry of result.data) {
        await callback(registry);
      }

      totalPages = Math.ceil(
        parseFloat(result.headers.total) /
          parseInt(result.headers["per-page"], 10)
      );
    }

    if (checkUserCancellation) {
      return { success: true };
    } else {
      return true;
    }
  }
}

export default BaseService;



// WEBPACK FOOTER //
// ./src/core/service/base.service.js